"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { useQuery } from "@tanstack/react-query";
import { Users, Search, UserPlus, ChevronLeft, ChevronRight } from "lucide-react";
import { rosterApi } from "@/shared/api/roster";
import type { RosterListItem, RosterMemberRole } from "@/shared/api/roster";
import { useMe } from "@/hooks/useUsers";
import { userApi } from "@/shared/api/user";
import { canApplyToRoster } from "@/features/roster-apply/model/can-apply";
import { ApplyToTeamModal } from "@/features/roster-apply/apply-to-team-modal";
import { normalizeLolRole } from "@/shared/ui/data-display/role-icon";
import { Button } from "@/shared/ui/buttons/button";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/shared/ui/feedback/alert";
import { Checkbox } from "@/shared/ui/inputs/checkbox";
import { Input } from "@/shared/ui/inputs/input";
import { Label } from "@/shared/ui/forms/label";
import { RosterCard } from "@/shared/ui/roster/roster-card";
import { rosterListItemToCard } from "@/shared/ui/roster/adapters";

const PAGE_SIZE = 20;

const LANES = ["top", "jungle", "mid", "adc", "support"] as const;

const LANE_TO_ROLE: Record<string, RosterMemberRole> = {
  top: "Top",
  jungle: "Jungle",
  mid: "Mid",
  adc: "Adc",
  support: "Support",
};

/**
 * Плитка каталога. Вид команды больше не живёт здесь — он общий с доской `/lfg`
 * и профилем (`@/shared/ui/roster`, KAN-814). Странице остаётся то, что есть
 * только у неё: заявка в команду и список свободных ролей для неё.
 */
function RosterGridCard({
  roster,
  canApply,
  priority = false,
}: {
  roster: RosterListItem;
  canApply: boolean;
  priority?: boolean;
}) {
  const tUi = useTranslations("Ui");
  const [applyOpen, setApplyOpen] = useState(false);
  const members = roster.members ?? [];
  // Свободные роли считаем по РЕАЛЬНО занятым позициям, а не по числу игроков.
  const filledRoles = new Set(
    members.map((m) => normalizeLolRole(m.role)).filter(Boolean),
  );

  // KAN-368: тренера здесь не было вовсе, хотя бэкенд заявку на него принимает —
  // и принимает наравне с запасным, отдельно оговаривая, что это не основной
  // состав (RosterApplicationService: «Доступны только запасные и тренер»).
  // То есть подать заявку тренером было нельзя исключительно из-за фронта.
  //
  // Тренер в команде один: если место занято, роль не предлагаем — так же, как
  // не предлагаем занятую линию.
  const hasCoach = members.some(
    (m) => String(m.role ?? "").toLowerCase() === "coach",
  );
  const freeRoles: RosterMemberRole[] = [
    ...LANES.filter((l) => !filledRoles.has(l)).map((l) => LANE_TO_ROLE[l]!),
    "Substitute",
    ...(hasCoach ? [] : (["Coach"] as RosterMemberRole[])),
  ];

  // Кнопка только если: можно подавать (авторизован, нет постоянной команды),
  // команда ищет игроков И есть свободное место (memberCount < maxMembers).
  // 5/5 стартеров не блокирует — на «Запас» подать можно; но при ПОЛНОМ составе
  // (включая запас/тренера) свободных мест нет — кнопку прячем.
  const teamMax = roster.maxMembers ?? 7;
  const showApply =
    canApply && roster.isLookingForPlayers && roster.memberCount < teamMax;

  return (
    <>
      <RosterCard
        variant="tile"
        priority={priority}
        roster={rosterListItemToCard(roster)}
        footerActions={
          showApply ? (
            <Button
              size="sm"
              className="w-full"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setApplyOpen(true);
              }}
            >
              <UserPlus className="h-4 w-4 mr-1.5" />
              {tUi("rosters.podatZayavku")}
            </Button>
          ) : undefined
        }
      />

      {showApply && (
        <ApplyToTeamModal
          open={applyOpen}
          onClose={() => setApplyOpen(false)}
          teamId={roster.id}
          teamName={roster.name}
          freeRoles={freeRoles}
        />
      )}
    </>
  );
}

function RosterCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <Skeleton className="h-[104px] w-full rounded-none" />
      <div className="flex flex-col gap-3.5 px-4 pb-4">
        <div className="flex items-end gap-3">
          <Skeleton className="-mt-[26px] size-[52px] shrink-0 rounded-full" />
          <Skeleton className="h-5 w-28" />
        </div>
        <div className="flex items-center gap-2">
          {LANES.map((r) => (
            <Skeleton key={r} className="size-[26px] rounded-md" />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
          <Skeleton className="h-8 w-28 rounded-full" />
          <Skeleton className="h-4 w-10" />
        </div>
      </div>
    </div>
  );
}

/** `serverFallback` — серверный список ссылок на время первой загрузки (SEO). */
export default function RostersPage({
  serverFallback,
}: {
  serverFallback?: React.ReactNode;
}) {
  const tCommon = useTranslations("Common");
  const tUi = useTranslations("Ui");
  const [page, setPage] = useState(1);
  const [lookingForPlayers, setLookingForPlayers] = useState(false);
  const [search, setSearch] = useState("");

  // Подать заявку может авторизованный пользователь без ПОСТОЯННОЙ команды
  // (временная Arena-команда не мешает). Бэкенд — финальный гард.
  const { data: me } = useMe();
  const { data: myTeams } = useQuery({
    queryKey: ["my-teams-for-apply", me?.id],
    queryFn: () => userApi.getUserTeams(String(me?.id)),
    enabled: Boolean(me?.id),
    staleTime: 60_000,
  });
  // KAN-368: здесь стоял дефолт `= []`, из-за которого «ещё не знаем» было
  // неотличимо от «команд нет»: пока запрос летел, hasPermanentTeam считался
  // false, кнопка «Подать заявку» успевала отрисоваться и исчезала после ответа.
  // Капитан команды видел её мигание на каждой загрузке списка.
  const canApply = canApplyToRoster({
    userId: me?.id,
    myTeams,
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["rosters-public", page, lookingForPlayers],
    queryFn: () =>
      rosterApi.getPublicRostersList({
        page,
        pageSize: PAGE_SIZE,
        ...(lookingForPlayers ? { isLookingForPlayers: true } : {}),
      }),
    staleTime: 30_000,
  });

  const allItems = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Client-side name search on current page results
  const items = search.trim()
    ? allItems.filter(
        (r) =>
          r.name.toLowerCase().includes(search.trim().toLowerCase()) ||
          r.tag.toLowerCase().includes(search.trim().toLowerCase()),
      )
    : allItems;

  const handleLookingChange = (checked: boolean) => {
    setLookingForPlayers(checked);
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="space-y-4 sm:space-y-6 rounded-2xl border border-border/40 bg-black/5 px-3 py-4 sm:px-4 sm:py-5 shadow-sm backdrop-blur-sm dark:bg-white/5 md:px-6">
          {/* Title */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight truncate">
                {tCommon("teams")}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {total > 0 ? tCommon("plural.teams", { count: total }) : null}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={tUi("rosters.poiskPoNazvaniyuIli")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="looking-for-players"
                checked={lookingForPlayers}
                onCheckedChange={(v) => handleLookingChange(Boolean(v))}
              />
              <Label htmlFor="looking-for-players" className="cursor-pointer text-sm">
                {tUi("rosters.ischutIgrokov")}
              </Label>
            </div>
          </div>

          {/* Content */}
          {isError ? (
            <Alert variant="destructive">
              <AlertTitle>{tUi("rosters.oshibka")}</AlertTitle>
              <AlertDescription>
                {(error as Error)?.message ?? tUi("litRosters.neUdalosZagruzitSpisok")}
              </AlertDescription>
            </Alert>
          ) : isLoading && serverFallback ? (
            serverFallback
          ) : isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <RosterCardSkeleton key={i} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <Users className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-muted-foreground">
                {lookingForPlayers
                  ? tUi("litRosters.netKomandIschuschihIgrokov")
                  : search.trim()
                    ? tUi("litRosters.komandyNeNaydeny")
                    : tUi("litRosters.komandPokaNet")}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {items.map((roster, index) => (
                <RosterGridCard key={roster.id} roster={roster} canApply={canApply} priority={index === 0} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!isLoading && !isError && totalPages > 1 && !search.trim() && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
